import { useCallback, useEffect, useState, type FC } from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'

import { Header } from 'app/KinerjaShared/components/Header'
import { InternalServerError } from 'utils/customError'
import { usePelaksanaanPraktikKinerja } from 'app/KinerjaPenilikPegawaiPage/hooks/usePelaksanaanPraktikKinerja'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import usePenilikPeriodGuard from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikPeriodGuard'
import CardForm from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/DiskusiTindakLanjut/components/CardForm'
import OnceLoaded from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/components/OnceLoaded'
import CardFormPreview from 'app/KinerjaPenilikPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardFormPreview'
import LoadingSpinner from '../components/LoadingSpinner'

import type {
  PraktikKinerjaDiskusiTindakLanjutFormValue,
  PraktikKinerjaFormName,
  PraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-penilik-pegawai'

type DiskusiTindakLanjutProps = {}

const formNameToGet: PraktikKinerjaFormName = 'pendampingan_sekolah'
const formNameToPost: PraktikKinerjaFormName = 'diskusi_tindak_lanjut'

const DiskusiTindakLanjut: FC<DiskusiTindakLanjutProps> = () => {
  const router = useRouter()
  const [isPageLoading, setPageLoading] = useState(true)

  // Entry Point Guards
  const { isPeriodSuccess, pelaksanaanGuard } = usePenilikPeriodGuard()

  // Prepare form values from the API
  const formValues =
    usePelaksanaanPraktikKinerja<PraktikKinerjaPendampinganFormValue>({
      formName: formNameToGet,
    })

  const formValuesForPost =
    usePelaksanaanPraktikKinerja<PraktikKinerjaDiskusiTindakLanjutFormValue>({
      formName: formNameToPost,
    })

  //  Check if the api call is successful, based on the formValuesForPost, since it holds the correct form name for this page
  const isSuccess = !formValuesForPost.isLoading && formValuesForPost.isSuccess

  const goBack = useCallback(() => {
    if (window.history.state.idx <= 1)
      router.push(
        `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
      )
    else router.back()
  }, [router])

  useEffect(() => {
    if (
      formValues.penilikSummaryHookResult.summaryFetchStatus === 'success' &&
      !formValuesForPost.isLoading &&
      !formValuesForPost.isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (formValuesForPost.isSuccess) {
      if (!formValuesForPost.penilikSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
      } else if (formValuesForPost.isLocked) {
        goBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.isLocked,
    formValues.isSuccess,
    formValues.penilikSummaryHookResult.isPelaksanaanState,
    formValues.penilikSummaryHookResult.summaryFetchStatus,
    formValuesForPost.isLoading,
    formValuesForPost.isLocked,
    formValuesForPost.isSuccess,
    formValuesForPost.penilikSummaryHookResult.isPelaksanaanState,
  ])

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
    } else if (isPeriodSuccess && isSuccess) {
      setPageLoading(false)
    }
  }, [isPeriodSuccess, pelaksanaanGuard.isOpen, router, isSuccess])

  if (isPageLoading) {
    return (
      <div className="px-3 mx-auto max-w-7xl py-9">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className="px-3 mx-auto max-w-7xl py-9 space-y-7">
        <Header
          title={
            <div className="flex items-center gap-x-3">
              Rencana Tindak Lanjut
              {formValues.isViewOnly && !formValues.isAppeal && (
                <Badge variant="success">Selesai</Badge>
              )}
              {formValues.isAppeal && (
                <Badge variant="neutral">Perubahan sedang diajukan</Badge>
              )}
            </div>
          }
        />

        <OnceLoaded loaded={isSuccess}>
          {!formValuesForPost.isViewOnly ? (
            <CardForm
              formValues={formValues}
              formValuesForPost={formValuesForPost}
              onSubmitSuccess={() => goBack()}
            />
          ) : (
            <CardFormPreview
              formValues={formValues}
              formName={formNameToPost}
            />
          )}
        </OnceLoaded>
      </div>
    </>
  )
}

export default DiskusiTindakLanjut
